import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { useAuthContext } from '~/shared/context/useAuthContext';

import {
  transformApiRuleToFrontend,
  type AutoscaleRule,
  type AutoscaleRuleFromApi,
} from '../../hooks/useAutoscaleRulesQuery';

const baseUrl = '/api/v1/volumes/autoscaling/rules';

/**
 * Fetches a single autoscaling rule with its dry-run report.
 *
 * `?dry_run=true` puts the endpoint into the canonical rule shape (same as one
 * element of the list endpoint), so the response carries `dry_run_report` with
 * per-category volumes alongside aggregates. The side panel uses this to refresh
 * after a mutation — the table snapshot (`rule.volumeData`) covers the open path.
 */
export function useAutoscaleRuleDetails(
  ruleId: string | null,
  enabled: boolean = true,
  accountId?: string
) {
  const api = useXFetch();
  const { accounts } = useAuthContext();

  return useQuery<AutoscaleRule | null>({
    queryKey: ['autoscaleRuleDetails', ruleId, accountId ?? null],
    enabled: enabled && !!ruleId,
    refetchOnWindowFocus: false,
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!ruleId) {
        return null;
      }

      const params = new URLSearchParams();
      if (accountId) {
        params.set('accounts', accountId);
      }
      params.set('dry_run', 'true');
      const response = await api<AutoscaleRuleFromApi>(`${baseUrl}/${ruleId}?${params.toString()}`);

      return transformApiRuleToFrontend(response, accounts);
    },
  });
}
